import { HsDefaultShader, HsPointsMaterial, HsShaderObjectType } from '@sibe/hyperscene';

/**
 * Texture unit the sprite points sample from. The renderer binds its own textures on units 0 to 2, so a texture
 * kept here stays bound across frames.
 */
const SPRITE_TEXTURE_UNIT: number = 4;

// u_pointSize is the sprite size in pixels at a distance of one unit, so sprites shrink with distance.
const VERTEX_CODE: string = `#version 300 es
precision highp float;

in vec3 a_positions;

uniform mat4 u_projectionMatrix;
uniform mat4 u_relativeMatrix;
uniform float u_pointSize;

out float v_fragDepth;

void main() {
  gl_Position = u_projectionMatrix * u_relativeMatrix * vec4(a_positions, 1.0);
  gl_PointSize = u_pointSize / gl_Position.w;
  v_fragDepth = 1.0 + gl_Position.w;
}
`;

const FRAGMENT_CODE: string = `#version 300 es
precision highp float;

uniform float u_logDepthBufFC;
uniform sampler2D u_spriteTexture;

in float v_fragDepth;

layout(location = 0) out vec4 fragmentColor;

void main() {
  vec4 color = texture(u_spriteTexture, vec2(gl_PointCoord.x, 1.0 - gl_PointCoord.y));
  if (color.a < 0.05) {
    discard;
  }
  gl_FragDepth = log2(v_fragDepth) * u_logDepthBufFC * 0.5;
  fragmentColor = color;
}
`;

/**
 * Draws every point of a point cloud as a camera-facing textured sprite in one draw call, the current-engine
 * replacement for the legacy instanced sprite collection.
 */
export function makeSpritePointsMaterial(
  gl: WebGL2RenderingContext,
  texture: WebGLTexture
): HsPointsMaterial<HsDefaultShader> {
  const shader: HsDefaultShader = new HsDefaultShader(HsShaderObjectType.single);
  shader.makeVertexCode = (): string => VERTEX_CODE;
  shader.makeFragmentCode = (): string => FRAGMENT_CODE;
  const material: HsPointsMaterial<HsDefaultShader> = new HsPointsMaterial(gl, shader);
  // The material compiled and selected the program, so the sampler uniform goes to this shader.
  shader.uniform1i(gl, 'u_spriteTexture', SPRITE_TEXTURE_UNIT);
  gl.activeTexture(gl.TEXTURE0 + SPRITE_TEXTURE_UNIT);
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.activeTexture(gl.TEXTURE0);
  return material;
}

/** The point size that makes a sprite `worldSize` units wide on a canvas `canvasHeight` pixels high. */
export function spritePointSize(worldSize: number, canvasHeight: number, fieldOfView: number): number {
  return (worldSize * canvasHeight) / (2 * Math.tan(fieldOfView / 2));
}
